import { get } from 'lodash';

type TTranslate = (key: string, params?: Record<string, string>) => unknown;

interface IServerMessageRule {
  pattern: RegExp;
  key: string;
  params?: (match: RegExpMatchArray) => Record<string, string>;
}

/**
 * The forgot and reset password endpoints answer with class-validator
 * constraint messages (`apps/api/src/manager/validators/*`,
 * `auth/validators/is-valid-password.validator.ts` in `platform/monorepo`):
 * plain English, no `slug`, no i18n key, and `x-lang` does not change them. The
 * exact sentence is the only thing the portal can match. Drop a rule the day
 * its validator returns an i18n path instead.
 */
const AUTH_SERVER_MESSAGE_RULES: IServerMessageRule[] = [
  {
    pattern: /^E-mail already sent, try again in few minutes\.$/,
    key: 'common.authServerMessages.emailRecentlySent'
  },
  {
    pattern: /^User not found\.$/,
    key: 'common.authServerMessages.userNotFound'
  },
  {
    pattern: /^Please enter a valid email address$/,
    key: 'common.authServerMessages.invalidEmail'
  },
  {
    pattern: /^Password reset token is invalid\.$/,
    key: 'common.authServerMessages.invalidResetToken'
  },
  {
    pattern: /^Password reset token expired\.$/,
    key: 'common.authServerMessages.expiredResetToken'
  },
  {
    pattern: /^The password length must be higher than (\d+) characters including at least a letter and a number$/,
    key: 'common.authServerMessages.weakPassword',
    params: match => ({ length: match[1] })
  }
];

export const translateAuthServerMessage = (
  message: string,
  translate: TTranslate
): string => {
  const trimmed = String(message).trim();

  for (const rule of AUTH_SERVER_MESSAGE_RULES) {
    const match = trimmed.match(rule.pattern);
    if (match) {
      return String(
        translate(rule.key, rule.params ? rule.params(match) : undefined)
      );
    }
  }

  return message;
};

/** The first readable sentence of a failed legacy request, or '' when there is none. */
export const getFirstServerMessage = (failedRequest: unknown): string => {
  const message = get(failedRequest, 'response.data.message');

  if (typeof message === 'string') return message;
  if (!Array.isArray(message)) return '';

  const first = message.find(
    (item: unknown) => typeof item === 'string' && !!item
  );

  return first || '';
};
